import { api } from './api';
// Error handling function
const handleApiError = (error: any) => {
  console.error('API Error:', error);
  return error;
};

export interface TransferItem {
  id?: string;
  productId: string;
  quantity: number;
  product?: {
    id: string;
    name: string;
    barcode?: string;
    price: number;
  };
}

export interface Transfer {
  id: string;
  fromLocationId: string;
  toLocationId: string;
  userId: string;
  status: TransferStatus;
  isDraft: boolean;
  transferNumber?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  fromLocation: {
    id: string;
    name: string;
  };
  toLocation: {
    id: string;
    name: string;
  };
  user?: {
    id: string;
    name: string;
  };
  items: TransferItem[];
}

export enum TransferStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  IN_TRANSIT = 'IN_TRANSIT',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export interface CreateTransferRequest {
  fromLocationId: string;
  toLocationId: string;
  items: Omit<TransferItem, 'id' | 'product'>[];
  notes?: string;
  isDraft?: boolean;
}

export interface UpdateTransferRequest {
  fromLocationId?: string;
  toLocationId?: string;
  items?: Omit<TransferItem, 'id' | 'product'>[];
  notes?: string;
  isDraft?: boolean;
}

export interface GetTransfersRequest {
  page?: number;
  limit?: number;
  status?: TransferStatus;
  fromLocationId?: string;
  toLocationId?: string;
  includeDrafts?: boolean;
}

export interface PaginatedTransfersResponse {
  data: Transfer[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export const transfersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTransfers: builder.query<PaginatedTransfersResponse, GetTransfersRequest>({
      query: (params) => ({
        url: '/transfers',
        params: {
          page: params.page || 1,
          limit: params.limit || 50,
          ...(params.status && { status: params.status }),
          ...(params.fromLocationId && { fromLocationId: params.fromLocationId }),
          ...(params.toLocationId && { toLocationId: params.toLocationId }),
          ...(params.includeDrafts && { includeDrafts: params.includeDrafts }),
        },
      }),
      providesTags: ['Transfer'],
      transformErrorResponse: handleApiError,
    }),

    getTransferById: builder.query<{ data: Transfer }, string>({
      query: (id) => `/transfers/${id}`,
      providesTags: (result, error, id) => [{ type: 'Transfer', id }],
      transformErrorResponse: handleApiError,
    }),

    getUserDrafts: builder.query<{ data: Transfer[] }, void>({
      query: () => '/transfers/drafts/me',
      providesTags: ['TransferDraft'],
      transformErrorResponse: handleApiError,
    }),

    createTransfer: builder.mutation<{ data: Transfer; message: string }, CreateTransferRequest>({
      query: (transfer) => ({
        url: '/transfers',
        method: 'POST',
        body: transfer,
      }),
      invalidatesTags: ['Transfer', 'TransferDraft'],
      transformErrorResponse: handleApiError,
    }),

    updateTransfer: builder.mutation<
      { data: Transfer; message: string },
      { id: string; data: UpdateTransferRequest }
    >({
      query: ({ id, data }) => ({
        url: `/transfers/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Transfer', 'TransferDraft'],
      transformErrorResponse: handleApiError,
    }),

    updateTransferStatus: builder.mutation<
      { message: string },
      { id: string; status: TransferStatus }
    >({
      query: ({ id, status }) => ({
        url: `/transfers/${id}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['Transfer'],
      transformErrorResponse: handleApiError,
    }),

    cancelTransfer: builder.mutation<{ message: string }, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/transfers/${id}/cancel`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Transfer'],
      transformErrorResponse: handleApiError,
    }),
  }),
});

export const {
  useGetTransfersQuery,
  useGetTransferByIdQuery,
  useGetUserDraftsQuery,
  useCreateTransferMutation,
  useUpdateTransferMutation,
  useUpdateTransferStatusMutation,
  useCancelTransferMutation,
} = transfersApi;